import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  Grid,
} from "@material-ui/core";
import { useFormik } from "formik";
import * as Yup from "yup";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

import Widget from "../../components/Widget/Widget";
import PageTitle from "../../components/PageTitle/PageTitle";
import SectionService from "./Locality/Service/sectionService";
import AddClassService from "./Locality/Service/addClassService";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#0f3b62ad",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    overflow: "auto",
  },
}));

export default function Section() {
  const tableHeaders = ["Class", "Section Name", "Status", "Edit", "Delete"];
  const classes = useStyles();

  const [sectionList, setSectionList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState({
    classId: "",
    sectionName: "",
    status: true,
  });

  useEffect(() => {
    getClassList();
    getSectionList();
  }, []);

  const getClassList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    AddClassService.getAllAddClass(userDetails.schooleId)
      .then((res) => {
        // safety: de‑duplicate by className in case backend returns duplicates
        const list = Array.isArray(res) ? res : [];
        const map = new Map();
        for (const item of list) {
          const key = String(item?.className || "").trim().toLowerCase();
          if (!key) continue;
          if (!map.has(key)) map.set(key, item);
        }
        const unique = Array.from(map.values()).sort((a, b) =>
          String(a?.className || "").localeCompare(String(b?.className || ""))
        );
        setClassList(unique);
      })
      .catch(() => setClassList([]));
  };

  const getSectionList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    SectionService.getAllSection(userDetails.schooleId)
      .then((res) => setSectionList(res || []))
      .catch(() => setSectionList([]));
  };

  const handleOpen = (reset = false) => {
    if (reset) {
      setSection({
        classId: "",
        sectionName: "",
        status: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const validationSchema = Yup.object().shape({
    classId: Yup.string().required("Class is required"),
    sectionName: Yup.string().required("Section Name is required"),
    status: Yup.boolean().required("Status is required"),
  });

  const editSection = (row) => {
    setSection({
      _id: row._id,
      classId: row.classId?._id || row.classId || "",
      sectionName: row.sectionName || "",
      status: row.status !== false,
    });
    setOpen(true);
  };

  const deleteSection = (row) => {
    if (!row) return;
    if (window.confirm("Are you sure you want to delete this section?")) {
      SectionService.deleteSection(row)
        .then(() => {
          getSectionList();
          alert("Section deleted successfully.");
        })
        .catch((err) => {
          alert(
            err?.response?.data?.message || "Failed to delete section. Please try again."
          );
        });
    }
  };

  const formik = useFormik({
    initialValues: section,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
      const payload = {
        schooleId: userDetails.schooleId,
        classId: values.classId,
        sectionName: values.sectionName,
        status: values.status,
      };

      if (section._id) {
        payload._id = section._id;
        SectionService.updateSection(payload)
          .then(() => {
            handleClose();
            getSectionList();
            resetForm();
            alert("Section updated successfully.");
          })
          .catch((err) => {
            alert(
              err?.response?.data?.message || "Failed to update section. Please try again."
            );
          });
      } else {
        SectionService.addSection(payload)
          .then(() => {
            getSectionList();
            handleClose();
            resetForm();
            alert("Section added successfully.");
          })
          .catch((err) => {
            alert(
              err?.response?.data?.message || "Failed to add section. Please try again."
            );
          });
      }
    },
  });

  return (
    <>
      <PageTitle
        title="Section"
        button={
          <Button
            variant="contained"
            onClick={() => handleOpen(true)}
            size="medium"
            color="secondary"
            style={{ backgroundColor: "rgb(15 59 98)" }}
          >
            Add Section
          </Button>
        }
      />

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget title="" upperTitle noBodyPadding bodyClass={classes.tableOverflow}>
            <Table className="mb-0">
              <TableHead>
                <TableRow>
                  {tableHeaders.map((key) => (
                    <StyledTableCell key={key}>{key}</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sectionList.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell className="pl-3 fw-normal">
                      {row.classId?.className || "-"}
                    </TableCell>
                    <TableCell className="pl-3 fw-normal">
                      {row.sectionName}
                    </TableCell>
                    <TableCell>{row.status ? "Active" : "Inactive"}</TableCell>
                    <TableCell>
                      <EditIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => editSection(row)}
                      />
                    </TableCell>
                    <TableCell>
                      <DeleteIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => deleteSection(row)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Widget>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{section._id ? "Edit Section" : "Add Section"}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent style={{ width: 308 }}>
            <FormControl
              variant="standard"
              fullWidth
              style={{ marginTop: 8, marginBottom: 8 }}
            >
              <InputLabel id="class-label">Class</InputLabel>
              <Select
                labelId="class-label"
                id="classId"
                name="classId"
                value={formik.values.classId}
                onChange={formik.handleChange}
                error={formik.touched.classId && Boolean(formik.errors.classId)}
              >
                <MenuItem value="">
                  <em>Select Class</em>
                </MenuItem>
                {classList.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              InputProps={{ style: { width: 258 } }}
              autoFocus
              margin="dense"
              id="sectionName"
              name="sectionName"
              label="Section Name"
              type="text"
              variant="standard"
              fullWidth
              value={formik.values.sectionName}
              onChange={formik.handleChange}
              error={formik.touched.sectionName && Boolean(formik.errors.sectionName)}
              helperText={formik.touched.sectionName && formik.errors.sectionName}
            />

            <FormControl variant="standard" fullWidth style={{ marginTop: 16 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                error={formik.touched.status && Boolean(formik.errors.status)}
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Save</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

